<template>
  <div
    class="benefiting"
    v-if="selectedProject"
  >
    <section
      class="banner"
      v-if="banner"
    >
      <div class="container align--center">
        <app-link
          class="back margin__b--6"
          :link="banner.cta"
          v-slot="{ text }"
        >
          <fa-icon
            class="margin__r--6 back__icon"
            :icon="['fal', 'long-arrow-left']"
          />

          {{ text }}
        </app-link>

        <h1
          class="title title__h1 bold"
          v-if="translate(banner.title)"
        >
          {{ translate(banner.title) }}
        </h1>
      </div>
    </section>

    <section
      class="stats"
      v-if="stats"
    >
      <div class="container stats__container align--center">
        <app-counter
          :metrics="stats.metrics"
          :query="statisticsQuery"
          v-if="benefitingId"
        />

        <ul class="links">
          <li
            class="links__li"
            v-if="translate(stats?.links?.project)"
          >
            <router-link
              :to="{ name: 'Project', params: { projectSlug: selectedProject.slug } }"
              class="btn btn__size--full-12 d-block btn__color--prim"
              :aria-label="selectedProject.name"
            >
              {{ translate(stats?.links?.project) }}
            </router-link>
          </li>

          <li
            class="links__li"
            v-if="stats?.links?.campaigns"
          >
            <app-link
              :link="stats?.links?.campaigns"
              :query="{ 'actions_data[project][benefiting]': benefitingId }"
              class="btn btn__size--full-12 d-block btn__solid--prim"
            />
          </li>
        </ul>
      </div>
    </section>

    <section-about
      :section="about"
      button-type="link"
      :fallback-image="selectedProject?.image.url"
      v-if="about"
    />

    <section
      class="actions"
      v-if="projects.length"
    >
      <div class="container align--center">
        <h2
          class="title title__h2 margin__b--24"
          v-if="translate(actions.title)"
        >
          {{ translate(actions.title) }}
        </h2>

        <div class="row align--left justify-content-center">
          <template
            v-for="project in projects"
            :key="project.id"
          >
            <section class="col-12 col-sm-6 col-md-3">
              <project-card
                class="project-card"
                :project="project"
              />
            </section>
          </template>
        </div>

        <app-link
          class="btn btn__color--prim btn__size--classic d-inline-block margin__t--24"
          :link="actions.cta"
          :query="{ 1: `benefiting_id[0]:${benefitingId}` }"
        />
      </div>
    </section>

    <section-partners
      :section="partners"
      v-if="partners"
    />
  </div>
</template>

<script setup>
import { computed, defineAsyncComponent } from 'vue'
import { useProjectStore } from '@/stores/ProjectStore'
import { useI18n } from '@/vendors/i18n'
import { get as _get, first as _first } from 'lodash'

const AppLink = defineAsyncComponent(() => import('&/atoms/AppLink'))
const AppCounter = defineAsyncComponent(() => import('&/molecules/AppCounter'))
const ProjectCard = defineAsyncComponent(() => import('&/organisms/ProjectCard'))
const SectionAbout = defineAsyncComponent(() => import('&/modules/pages/beplanet/SectionAboutBeplanet'))
const SectionPartners = defineAsyncComponent(() => import('&/modules/pages/beplanet/SectionPartnersBeplanet'))

const { sections } = defineProps({
  sections: { type: Object, required: true }
})

const { translate } = useI18n()
const { getProjects, getProject } = useProjectStore()

const { settings, banner, stats, about, actions, partners } = sections.attributes

const projectId = _first(settings?.project_id)
const { project: selectedProject } = getProject({ id: projectId, dependencies: [projectId] })

const benefitingId = computed(() => _get(selectedProject.value, 'benefiting.id'))
const statisticsQuery = computed(() => ({ ...stats?.query, benefiting_id: [benefitingId.value] }))
const projectsQuery = computed(() => ({ per_page: 4, sort_by: 'amount_first', benefiting_id: [benefitingId.value], ..._get(actions, 'query', {}) }))

const { projects } = getProjects({ query: projectsQuery, dependencies: [actions, benefitingId] })
</script>

<style lang="scss" scoped>
$color--bg: #FFFFFF;
$color--primary: #46935A;
$color--secondary: #30643D;
$color--tertiary: #F3FFD3;

.no-thumb {
  font-size: rem(72px);
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}


:deep() {
  .btn {
    line-height: 1.18;

    &__size {
      &--classic {
        padding-top: 12px;
        padding-bottom: 12px;
        border-radius: 30px;
        font-size: rem(18px);

        @include mq(md) {
          padding-top: 16px;
          padding-bottom: 16px;
          font-size: rem(20px);
        }
      }

      &--full-12 {
        border-radius: 100px;
        border-width: 2px;
        font-size: rem(22px);

        &.btn__color--prim {
          color: #FFFEF5
        }

        @include mq(sm) {
          padding: 16px 35px;
          border-width: 4px;
          font-size: rem(24px);
        }

        @include mq(lg) {
          font-size: rem(32px);
        }
      }
    }

    &__solid {
      &--prim {
        background: $color--bg;
      }
    }
  }
}

.title {
  font-family: $font-family--secondary;
  color: #4C5562;
  position: relative;
  z-index: 10;

  &__h1 {
    font-weight: 700;
    font-size: rem(32px);
    line-height: 1.208;

    @include mq(md) {
      font-size: rem(48px);
    }
  }

  &__h2 {
    font-weight: 700;
    font-size: rem(28px);
    line-height: 1.208;

    @include mq(md) {
      font-size: rem(32px);
    }
  }
}

.benefiting {
  background: $color--bg;
  color: #979CA4;
  font-size: rem(18px);
  line-height: 1.208;

  & > *:last-child {
    padding-bottom: 60px;
  }
}

.banner {
  padding: 24px 0;

  :deep(.back) {
    display: block;
    color: #979CA4;
    transition: all .3s ease-in-out;

    .back__icon {
      transition: all .3s ease-in-out;
      color: #979CA4;
    }

    &:hover {
      color: $color--primary;

      .back__icon {
        transform: translateX(-10px);
        color: $color--primary;
      }
    }
  }
}

.stats {
  padding-bottom: 50px;

  &__container {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;

    @include mq(md) {
      grid-template-columns: auto 300px;
    }

    @include mq(lg) {
      grid-template-columns: auto 360px;
    }

    @include mq(lg) {
      gap: 50px;
    }
  }

  :deep() {
    .counter {
      &__ul {
        gap: 32px;
        display: grid;
        grid-template-columns: 1fr;
        font-size: rem(24px);
        line-height: 0.75;
        color: $color--secondary;
        background: $color--tertiary;
        border-radius: 20px;
        padding: 48px 12px;

        @include mq(sm) {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-wrap: wrap;
          gap: 24px;
        }

        @include mq(md) {
          padding: 110px 12px;
        }
      }

      &__li {
        flex: 1;
      }

      &__value {
        font-size: rem(50px);
        line-height: 0.3;
        white-space: nowrap;

        @include mq(lg) {
          font-size: rem(80px);
        }
      }

      &__text {
        display: block;
        margin-top: 18px;
      }
    }
  }

  .links {
    padding: 12px 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 24px;
  }
}

.actions {
  padding: 30px 0;
}
</style>